'use client';

import type { GestureResponderEvent, View } from 'react-native';

import {
  checkIsNative,
  cn,
  Div,
  PlatformOS,
  Portal,
  PressableProps,
  ScrollView,
  Ul,
  useSafeAreaInsets,
  useWindowSize,
} from '@scatterlab/zeta-core';
import {
  Children,
  cloneElement,
  Context,
  FC,
  isValidElement,
  memo,
  MouseEvent,
  PropsWithChildren,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import useOverflowHidden from '../hooks/useOverflowHidden';

import type { DropdownPosition } from '../hooks/useDropdownMenu';

export type DropdownMenuProps = Partial<DropdownPosition> & {
  open?: boolean;
  onClose?: () => void;
  containerClassName?: string;
  className?: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  contexts?: Context<any>[];
  scrollable?: boolean;
};

const OptionalScrollView = memo(
  ({
    children,
    scrollable,
  }: {
    children: React.ReactNode;
    scrollable: boolean;
  }) => (scrollable ? <ScrollView>{children}</ScrollView> : children),
);

const DropdownMenu: FC<PropsWithChildren<DropdownMenuProps>> = ({
  open,
  onClose,
  containerClassName,
  className,
  children,
  top: topAsProps,
  right,
  left,
  contexts,
  scrollable = false,
}) => {
  const { top } = useSafeAreaInsets();
  const listRef = useRef<HTMLUListElement | View>(null);
  const { height } = useWindowSize();
  const [bottom, setBottom] = useState<undefined | number>();

  useEffect(() => {
    const calculateBottom = () => {
      const listElement = listRef.current;
      if (!listElement || !open) return;
      if ('isConnected' in listElement) {
        const clientRect = listElement.getBoundingClientRect();
        if (clientRect.bottom > height) {
          setBottom(0);
        } else {
          setBottom(undefined);
        }
      } else {
        listElement.measureInWindow((_, y, __, measuredHeight) => {
          if (y + measuredHeight > height) {
            setBottom(0);
          } else {
            setBottom(undefined);
          }
        });
      }
    };
    setTimeout(calculateBottom);
  }, [height, open]);

  const position = useMemo(() => {
    if (checkIsNative()) {
      return {
        top: (topAsProps ?? top) - (PlatformOS === 'android' ? 0 : top),
        right,
        left,
        bottom,
      };
    }

    return {
      top: `${topAsProps ?? top}px`,
      right: `${right}px`,
      left: `${left}px`,
      bottom: bottom !== undefined ? `${bottom}px` : undefined,
    };
  }, [right, top, left, topAsProps, bottom]);

  useOverflowHidden(open);

  if (!open) return null;

  return (
    <Portal contexts={contexts}>
      <Div
        className="absolute inset-0 z-[60] flex flex-1 flex-col"
        style={{ top: 0, left: 0, right: 0, bottom: 0 }}
      >
        <Div
          className="relative flex min-h-0 flex-1 flex-col"
          onPress={(event) => {
            event.stopPropagation();
            event.preventDefault();
            onClose?.();
          }}
        >
          <Div
            className={cn(
              'absolute right-0 top-3 z-[60] rounded-[12px] bg-gray-sub1 shadow-400',
              containerClassName,
            )}
            style={position}
          >
            <Ul
              ref={listRef}
              className={cn('flex w-[168px] flex-col', className)}
              onPress={(event) => {
                event.preventDefault();
                event.stopPropagation();
              }}
            >
              <OptionalScrollView scrollable={scrollable}>
                {Children.toArray(children).map((child, index) =>
                  isValidElement(child)
                    ? cloneElement(child, {
                        // eslint-disable-next-line react/no-array-index-key
                        key: index,
                        onPress: (
                          event: GestureResponderEvent | MouseEvent,
                        ) => {
                          (child.props as PressableProps).onPress?.(event);
                          onClose?.();
                        },
                      } as never)
                    : child,
                )}
              </OptionalScrollView>
            </Ul>
          </Div>
        </Div>
      </Div>
    </Portal>
  );
};

export default memo(DropdownMenu);
